// =============================================================================
// Cliente da API
//
// O front nunca fala com o banco. Tudo passa por aqui, e o formato de erro é o
// que a API já padronizou em api/src/erros.ts — inclusive os sete SQLSTATE
// NFxxx do contrato, que chegam com mensagem escrita para o Financeiro ler.
// =============================================================================

import type { Ciclo, Cotacao } from "./ferramentaCiclo.ts";
import type { Familia, LinhaOrcamento } from "./ferramentasAnalise.ts";
import type { SituacaoLinha, StatusArea } from "./proposta.ts";
import { EMBUTIDO, EVENTO_SEM_SESSAO } from "./embutido.ts";

export type ErroApi = { codigo: string; mensagem: string; detalhe?: string };

export class FalhaApi extends Error {
  readonly status: number;
  readonly codigo: string;
  readonly detalhe: string | undefined;

  constructor(status: number, erro: ErroApi) {
    super(erro.mensagem);
    this.name = "FalhaApi";
    this.status = status;
    this.codigo = erro.codigo;
    this.detalhe = erro.detalhe;
  }
}

async function pedir<T>(caminho: string, init?: RequestInit): Promise<T> {
  let resp: Response;
  try {
    resp = await fetch(caminho, {
      ...init,
      headers: { "content-type": "application/json", ...(init?.headers ?? {}) },
    });
  } catch {
    // Rede caiu, ou a API não está de pé. Distinguir isto de erro de negócio
    // importa: a ação do usuário é outra — aqui é tentar de novo, não corrigir
    // o que digitou.
    throw new FalhaApi(0, {
      codigo: "SEM_CONEXAO",
      mensagem: "Não foi possível falar com o servidor. Verifique a conexão e tente de novo.",
    });
  }

  // ⚠️ 401 não é erro de tela, é ausência de sessão — e o único desfecho útil é
  // mandar a pessoa ao login. Sem isto o sistema fica inacessível em produção:
  // a tela carrega, chama a API, toma 401 e mostra "Sessão não encontrada",
  // sem nenhum caminho para entrar. Foi o que aconteceu no primeiro acesso pelo
  // domínio, em 16/09.
  //
  // `replace` e não `href`: assim o login não entra no histórico e o botão
  // "voltar" não devolve a pessoa a uma tela morta.
  if (resp.status === 401) {
    // Dentro do Arena (22/10), ir ao Google na própria página deixaria a
    // moldura em branco: a tela mostra o botão de entrar pela janelinha.
    if (EMBUTIDO) {
      window.dispatchEvent(new Event(EVENTO_SEM_SESSAO));
      throw new FalhaApi(401, { codigo: "SEM_SESSAO", mensagem: "Entre com a conta Google para ver o painel." });
    }
    // Sem "voltar para onde eu estava": a navegação é por estado, não por URL
    // (App.tsx), então todo mundo aterrissa na mesma tela de qualquer forma.
    // Carregar um destino pelo fluxo de OAuth adicionaria peça móvel à
    // autenticação em troca de nada.
    location.replace("/auth/login");
    // A navegação é assíncrona; esta promessa nunca resolve, para que nenhuma
    // tela renderize um estado de erro durante a saída.
    await new Promise(() => {});
  }

  if (!resp.ok) {
    let corpo: { erro?: ErroApi } | null = null;
    try {
      corpo = await resp.json();
    } catch {
      /* resposta sem JSON: cai no genérico abaixo */
    }
    throw new FalhaApi(
      resp.status,
      corpo?.erro ?? {
        codigo: "DESCONHECIDO",
        mensagem: "Não foi possível concluir a operação.",
      },
    );
  }

  return (await resp.json()) as T;
}

export const api = {
  get: <T>(caminho: string) => pedir<T>(caminho),
  post: <T>(caminho: string, corpo: unknown) =>
    pedir<T>(caminho, { method: "POST", body: JSON.stringify(corpo) }),
};

// ─── formas que as views devolvem (docs/CONTRATO_DADOS.md §2) ────────────────
// Dinheiro é string de centavos; percentual é string de numeric(7,2);
// competência é date no dia 1. Nada disto vira número aqui — ver lib/formato.ts.

export type LinhaMes = {
  /** ⚠️ NULO quando a linha da tabela representa várias competências (filtro de
   *  período com mais de um mês). Justificar, pedir revisão e abrir o drilldown
   *  são atos sobre a linha de UM mês; sem id, a tela esconde a ação em vez de
   *  aplicá-la a um mês escolhido a esmo. */
  linha_id: string | null;
  exercicio_ano: number;
  centro_custo_id: string;
  centro_custo_codigo: string;
  centro_custo_nome: string;
  categoria_id: string;
  categoria_nome: string;
  competencia: string;
  tipo: string;
  fornecedor_id: string | null;
  fornecedor_nome: string | null;
  orcado_centavos: string;
  realizado_centavos: string;
  comprometido_centavos: string;
  disponivel_centavos: string;
  pct_execucao: string;
  status: string;
};

/**
 * O gasto de uma categoria que não chegou a nenhuma linha orçamentária no
 * período, por centro de custo: o realizado da coordenada (044) menos o que as
 * linhas dela receberam. Não é linha: não há o que justificar, e o drilldown é
 * pela coordenada, não pelo `linha_id`.
 */
export type SemLinhaMes = {
  centro_custo_id: string;
  centro_custo_codigo: string;
  centro_custo_nome: string;
  /** Nulo no lançamento sem categoria, que aparece como "(sem categoria)". */
  categoria_id: string | null;
  categoria_nome: string;
  /** Falso: a categoria não tem orçamento no mês — o "não orçado". Verdadeiro:
   *  tem, e o lançamento ainda espera ser atribuído a uma linha. */
  tem_orcamento: boolean;
  /** A última competência do período — a única, quando o período é um mês. */
  competencia: string;
  realizado_centavos: string;
  comprometido_centavos: string;
  disponivel_centavos: string;
};

export type CentroCustoMes = {
  centro_custo_id: string;
  codigo: string;
  nome: string;
  competencia: string;
  orcado_centavos: string;
  realizado_centavos: string;
  comprometido_centavos: string;
  disponivel_centavos: string;
  pct_execucao: string;
  qtd_linhas_excedentes: number;
};

export type Forecast = {
  linha_id: string;
  centro_custo_id: string;
  categoria_id: string;
  competencia: string;
  tipo: string;
  base_calculo: string;
  previsto_centavos: string;
};

export type Ritmo = {
  linha_id: string;
  centro_custo_id: string;
  competencia: string;
  realizado_centavos: string;
  ritmo_esperado_centavos: string;
  desvio_ritmo_centavos: string;
  pct_desvio_ritmo: string;
  dias_decorridos: number;
  dias_no_mes: number;
};

export type Heatmap = {
  centro_custo_id: string;
  centro_custo_nome: string;
  categoria_id: string;
  categoria_nome: string;
  competencia: string;
  pct_consumo: string;
  faixa: "ok" | "atencao" | "estouro";
};

export type Ranking = {
  competencia: string;
  centro_custo_id: string;
  /** Nulo na linha que soma uma categoria de pessoal para quem não é do
   *  Financeiro (migration 051): ela não é um lançamento, é um total. */
  lancamento_id: string | null;
  descricao: string | null;
  fornecedor_nome: string | null;
  categoria_nome: string;
  valor_centavos: string;
  posicao: number;
};

export type Desvio = {
  linha_id: string;
  centro_custo_id: string;
  categoria_id: string;
  competencia: string;
  orcado_centavos: string;
  realizado_centavos: string;
  desvio_centavos: string;
  pct_desvio: string;
  status: string;
  tem_justificativa: boolean;
  tolerancia_ate: string | null;
};

export type Lancamento = {
  /** Nulo na linha que soma uma categoria de pessoal para quem não é do
   *  Financeiro (migration 051): ela não é um lançamento, é um total. */
  lancamento_id: string | null;
  linha_id: string | null;
  despesa_orcada_id: string | null;
  competencia: string;
  data_vencimento: string | null;
  data_pagamento: string | null;
  descricao: string | null;
  fornecedor_nome: string | null;
  categoria_nome: string;
  centro_custo_id: string;
  valor_rateado_centavos: string;
  valor_total_centavos: string;
  /** Nulo quando o lançamento não está alocado a linha nenhuma (o não orçado)
   *  e na linha de total de pessoal. */
  metodo: "chave" | "texto" | "manual" | "regra" | null;
  confianca: "alta" | "media" | "baixa" | null;
  score: string | null;
  estado_alocacao: "sugerida" | "confirmada" | null;
  ausente_na_origem_em: string | null;
  /** Migration 064: confirmada por uma pessoa (com `confirmado_por`). Confirmada
   *  sem pessoa é o motor — chave de alta confiança ou regra do Financeiro. */
  confirmado_por_pessoa?: boolean | null;
  /** Só no detalhamento por coordenada, que pode trazer vários CCs. */
  centro_custo_codigo?: string;
  centro_custo_nome?: string;
  /** A compra inteira, quando este lançamento é uma das partes de uma compra
   *  dividida entre categorias (só para quem tem acesso total). */
  compra_partes?: number | null;
  /** Quantas categorias a compra tem: menos que as partes quando alguma
   *  categoria foi dividida entre centros de custo (27/10). */
  compra_categorias?: number | null;
  compra_total_centavos?: string | null;
};

/** Agregado POR CENTRO DE CUSTO. A view `v_execucao_ytd` tem granularidade de
 *  linha; a soma acontece na rota (api/src/rotas/orcamento.ts), pelo mesmo
 *  critério do consolidado — somar subtotais que a view já produziu. */
export type Ytd = {
  exercicio_ano: number;
  centro_custo_id: string;
  ate_competencia: string;
  orcado_ytd_centavos: string;
  realizado_ytd_centavos: string;
  comprometido_ytd_centavos: string;
  disponivel_ytd_centavos: string;
  pct_execucao_ytd: string;
};

export type PainelGestor = {
  competencia: string;
  /** As competências somadas nesta resposta. Uma, no caso de sempre. */
  competencias?: string[];
  exercicio: number;
  centros: CentroCustoMes[];
  linhas: LinhaMes[];
  /** O gasto que não chegou a nenhuma linha orçamentária no período. Linhas +
   *  isto fecham com o realizado e o comprometido dos cartões. */
  sem_linha?: SemLinhaMes[];
  forecast: Forecast[];
  ritmo: Ritmo[];
  heatmap: Heatmap[];
  ranking: Ranking[];
  desvios: Desvio[];
  /** v_execucao_cc_mes do exercício inteiro — gráfico mês a mês e tendência. */
  serie_ano: CentroCustoMes[];
  /** v_execucao_ytd até a competência — os KPIs anuais. */
  ytd: Ytd[];
};

export type Linhagem = {
  fonte: string;
  ultima_sincronizacao: string | null;
  ultima_competencia_completa: string | null;
  registros: string;
  status: "ok" | "atrasado" | "falha";
  detalhe: string | null;
};

export type CategoriaMes = {
  categoria_id: string;
  categoria_nome: string;
  natureza: string;
  centro_custo_id: string;
  competencia: string;
  orcado_centavos: string;
  realizado_centavos: string;
  comprometido_centavos: string;
  disponivel_centavos: string;
  pct_execucao: string;
  status: string;
};

export type Consolidado = {
  orcado_centavos: string;
  realizado_centavos: string;
  comprometido_centavos: string;
  disponivel_centavos: string;
  qtd_linhas_excedentes: number;
  qtd_centros: number;
};

export type VisaoFinanceiro = {
  competencia: string;
  /** As competências somadas nesta resposta. Uma, no caso de sempre. */
  competencias?: string[];
  exercicio: number;
  consolidado: Consolidado;
  centros: CentroCustoMes[];
  categorias: CategoriaMes[];
  serie_ano: CentroCustoMes[];
  heatmap: Heatmap[];
  desvios: Desvio[];
  excecoes_abertas: number;
  /** ⚠️ Nulo quando a competência não tem lançamento nenhum: a view agrupa por
   *  competência, e mês vazio não gera linha. */
  cobertura: CoberturaRealizado | null;
};

/**
 * Quanto do realizado do mês está representado nesta tela.
 *
 * Toda view da Visão Financeiro agrega por centro de custo, e lançamento sem CC
 * vinculado não entra em nenhuma. O número exibido continua certo; o que estava
 * errado era a leitura de que ele fosse o total.
 */
export type CoberturaRealizado = {
  lancamentos: number;
  sem_centro_custo: number;
  valor_centavos: string;
  valor_sem_centro_custo: string;
  /** ⚠️ É por AQUI que se mede a fatia, não por `valor_centavos`: o KPI
   *  "Realizado" mostra o pago. Comparar com o valor total compararia o
   *  realizado com o total contratado. */
  pago_centavos: string;
  pago_sem_centro_custo: string;
};

/**
 * O período da tela: uma competência ou várias.
 *
 * Uma só continua indo como `competencia=`, que é o que o contrato documenta e
 * o que a API responde pelo caminho antigo, sem `group by`. Várias vão em
 * `competencias=`, e aí os números somam a janela.
 */
function paramPeriodo(periodo: string[]): string {
  return periodo.length === 1
    ? `competencia=${periodo[0]}`
    : `competencias=${periodo.join(",")}`;
}

export const buscarVisaoFinanceiro = (periodo: string[]) =>
  api.get<VisaoFinanceiro>(`/api/visao-financeiro?${paramPeriodo(periodo)}`);

export const buscarPainelGestor = (periodo: string[]) =>
  api.get<PainelGestor>(`/api/painel-gestor?${paramPeriodo(periodo)}`);

/** Um alerta do AF-01 que ainda vale (v_alertas, migration 053). */
export type Alerta = {
  alerta_id: string;
  linha_id: string;
  centro_custo_id: string;
  centro_custo_codigo: string;
  centro_custo_nome: string;
  categoria_id: string;
  categoria_nome: string;
  competencia: string;
  gatilho: "limite_mes" | "variacao_fixa" | "consumo_addon";
  orcado_centavos: string;
  realizado_centavos: string;
  pct_consumido: string;
  pct_alerta_fixo: string;
  pct_alerta_variavel: string;
  /** Última justificativa da linha no mês; só "uso pontual" tira o alerta. */
  justificativa: string | null;
  /**
   * Impacto projetado no ano (migration 059, AF-01 §8.1). Só no alerta de
   * mensalidade (`variacao_fixa`); nulos nos outros.
   *   impacto = aumento mensal × meses do alerta até dezembro
   *   folga   = orçado da categoria no CC antes do alerta − realizado antes dele
   *   excede  = max(impacto − folga, 0)
   */
  aumento_mensal_centavos: string | null;
  meses_restantes: number | null;
  impacto_ano_centavos: string | null;
  folga_ano_centavos: string | null;
  excede_ano_centavos: string | null;
  /**
   * Até quando o gestor justifica o teto atingido (migration 063): 1 mês a
   * partir do aviso. Nulo nos outros gatilhos e depois da justificativa.
   * Opcional: a API anterior à 063 não manda.
   */
  prazo_justificativa?: string | null;
};

export const buscarAlertas = (periodo: string[], centroCustoId?: string) =>
  api.get<{ alertas: Alerta[] }>(
    `/api/alertas?${paramPeriodo(periodo)}${centroCustoId ? `&centro_custo_id=${centroCustoId}` : ""}`);

/** Faixa do mês na tendência — a mesma régua do mapa de calor e dos cartões. */
export type FaixaTendencia = "estouro" | "atencao" | "ok" | "sem_orcamento" | "sem_movimento";

/** Um mês de uma categoria de um CC na tendência de execução (migration 060). */
export type MesTendencia = {
  centro_custo_id: string;
  centro_custo_codigo: string;
  centro_custo_nome: string;
  categoria_id: string;
  categoria_codigo: string | null;
  categoria_nome: string;
  competencia: string;
  orcado_centavos: string;
  realizado_centavos: string;
  /** Nulo quando o mês não tem orçado. */
  pct_execucao: string | null;
  faixa: FaixaTendencia;
  /** Meses seguidos de estouro que terminam neste mês (0 se este não estourou). */
  meses_seguidos_estouro: number;
};

/** Os seis meses que terminam no último mês do período. */
export const buscarTendencia = (periodo: string[], centroCustoId?: string) =>
  api.get<{ referencia: string; meses: MesTendencia[] }>(
    `/api/tendencia?${paramPeriodo(periodo)}${centroCustoId ? `&centro_custo_id=${centroCustoId}` : ""}`);

/**
 * Receita (Visão Executiva) — só o ORÇADO.
 *
 * ⚠️ Não há campo de realizado, forecast nem resultado nesta resposta, e isso é
 * a forma refletindo o estado do sistema: receita realizada depende da ingestão
 * de contas a receber, que não existe (CLAUDE.md §8.3). Um campo zerado seria
 * pior que campo ausente — a tela desenharia receita nenhuma em vez de dizer
 * que ainda não sabe.
 */
export type ReceitaOrcada = {
  competencia: string;
  competencias: string[];
  exercicio: number;
  consolidado: { orcado_centavos: string; qtd_categorias: number; qtd_centros: number } | null;
  /** Receita realizada no período (061): o recebido é o realizado. */
  realizado: {
    lancado_centavos: string;
    recebido_centavos: string;
    a_receber_centavos: string;
    qtd_lancamentos: number;
  } | null;
  /** Os meses do exercício. Realizado NULO no mês sem lançamento. */
  serie_ano: {
    competencia: string;
    orcado_centavos: string;
    qtd_categorias: number;
    recebido_centavos: string | null;
    lancado_centavos: string | null;
  }[];
  categorias: {
    /** Nulo no realizado lançado sem categoria. */
    categoria_id: string | null;
    categoria_codigo: string | null;
    categoria_nome: string;
    orcado_centavos: string;
    lancado_centavos: string | null;
    recebido_centavos: string | null;
    a_receber_centavos: string | null;
  }[];
  /** Centro de custo × categoria no período — a lista lateral do Cadastrar. */
  linhas: {
    centro_custo_id: string;
    centro_custo_codigo: string;
    centro_custo_nome: string;
    categoria_id: string;
    categoria_codigo: string | null;
    categoria_nome: string;
    orcado_centavos: string;
  }[];
};

export const buscarReceita = (periodo: string[]) =>
  api.get<ReceitaOrcada>(`/api/receita?${paramPeriodo(periodo)}`);

export const buscarLancamentosDaLinha = (linhaId: string) =>
  api.get<{ lancamentos: Lancamento[] }>(`/api/linhas/${linhaId}/lancamentos`);

export const buscarLinhagem = () => api.get<{ fontes: Linhagem[] }>("/api/linhagem");

/** Uma linha do Histórico (v_movimentacao). `antes`/`depois` são o que a ação
 *  gravou — objeto na maioria, lista quando a ação atinge vários registros. */
export type Movimentacao = {
  id: string;
  momento: string;
  ator_id: string;
  ator_email: string | null;
  acao: string;
  entidade: string;
  entidade_id: string | null;
  antes: unknown;
  depois: unknown;
  origem: string;
  reverte_id: string | null;
  /** Migration 035: a ação é das que se desfazem e ninguém a reverteu ainda. */
  reversivel: boolean;
  /** Se já foi revertida, o registro da reversão. */
  revertida_por: string | null;
};

/** Migration 036: para onde a sugestão do motor pode ir. */
export type CandidatosAlocacao = {
  lancamento: {
    id: string;
    descricao: string | null;
    fornecedor: string | null;
    categoria: string | null;
    competencia: string;
    valor_centavos: string;
    centros_custo: { codigo: string; nome: string; valor_centavos: string }[];
    /** Migration 040: a descrição normalizada — o que a regra lembra. */
    padrao: string | null;
  };
  atual: {
    alocacao_id: string;
    linha_id: string;
    despesa_orcada_id: string | null;
    estado: "sugerida" | "confirmada";
    metodo: "chave" | "texto" | "manual" | "regra";
    confianca: "alta" | "media" | "baixa";
    score: number | null;
  } | null;
  candidatos: {
    linha_id: string;
    centro_custo: string;
    categoria: string;
    competencia: string;
    valor_centavos: string;
    fornecedor: string | null;
    tem_fornecedor_igual: boolean;
    subcategorias: { id: string; nome: string; valor_centavos: string; similaridade: number | null }[];
  }[];
  /** Migration 040: outros lançamentos esperando decisão com o mesmo CC,
   *  categoria e fornecedor — a quem a mesma escolha pode ser aplicada. */
  iguais: {
    lancamento_id: string;
    descricao: string | null;
    competencia: string;
    valor_centavos: string;
    mesma_descricao: boolean;
    /** Nomes das subcategorias disponíveis no mês DELE. */
    subcategorias: string[];
  }[];
};

/** Resultado de aplicar aos iguais (migration 040). */
export type Aplicacao = {
  aplicadas: number;
  ignoradas: { lancamento_id: string; motivo: string }[];
  regras: number;
} | null;

type AplicarAosIguais = { aplicar_a?: string[]; lembrar?: boolean };

export const buscarCandidatos = (lancamentoId: string) =>
  api.get<CandidatosAlocacao>(`/api/lancamentos/${lancamentoId}/candidatos`);

export const confirmarAlocacao = (corpo: { lancamento_id: string; linha_id: string; despesa_orcada_id: string | null } & AplicarAosIguais) =>
  api.post<{ alocacao_id: string; aplicacao: Aplicacao }>("/api/alocacoes/confirmar", corpo);

export const realocarAlocacao = (corpo: {
  lancamento_id: string; linha_id: string; despesa_orcada_id: string | null; motivo: string;
} & AplicarAosIguais) => api.post<{ alocacao_id: string; aplicacao: Aplicacao }>("/api/alocacoes/realocar", corpo);

export const reverterMovimentacao = (id: string, motivo: string) =>
  api.post<{ movimentacao_id: string }>(`/api/movimentacoes/${id}/reverter`, { motivo });

export type FiltroHistorico = {
  acoes?: string[];
  desde?: string;
  ate?: string;
  soPessoas?: boolean;
};

export const buscarMovimentacoes = (f: FiltroHistorico, limit: number, offset: number) => {
  const p = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (f.acoes?.length) p.set("acoes", f.acoes.join(","));
  if (f.desde) p.set("desde", f.desde);
  if (f.ate) p.set("ate", f.ate);
  if (f.soPessoas) p.set("so_pessoas", "1");
  return api.get<{ movimentacoes: Movimentacao[] }>(`/api/movimentacoes?${p}`);
};

export const registrarJustificativa = (corpo: {
  linha_id: string;
  competencia: string;
  motivo: string;
  texto: string;
  duracao_meses?: number | null;
}) => api.post<{ justificativa_id: string }>("/api/justificativas", corpo);

export const solicitarRevisao = (corpo: {
  linha_id: string;
  valor_solicitado_centavos: string;
  motivo: string;
}) => api.post<{ solicitacao_id: string; aviso: string }>("/api/revisoes", corpo);

// ─── Orçado vs Realizado, Solicitações e Avisos (validado em 23/09) ──────────
// Contrato das peças novas do banco: docs/HANDOFF_005_SOLICITACOES_E_AVISOS.md.

/** Uma célula da planilha Orçado vs Realizado: CC × categoria × competência. */
export type OrcadoRealizadoLinha = {
  centro_custo_id: string;
  centro_custo_codigo: string;
  centro_custo_nome: string;
  /** Nulo no lançamento sem categoria (migration 044), que aparece como "(sem categoria)". */
  categoria_id: string | null;
  categoria_codigo: string | null;
  categoria_nome: string;
  natureza: string;
  competencia: string;
  orcado_centavos: string;
  /** Nulo quando a origem ainda não informa realizado (receita). */
  realizado_centavos: string | null;
  comprometido_centavos: string | null;
};

/** Migration 064: a conciliação de cada coordenada. Nulos numa categoria de
 *  pessoal para quem não tem acesso total (051). */
export type ConciliacaoCoordenada = {
  centro_custo_id: string;
  categoria_id: string | null;
  competencia: string;
  qtd_lancamentos: number | null;
  qtd_automatico: number | null;
  qtd_confirmado: number | null;
  qtd_aguardando: number | null;
  qtd_sem_alocacao: number | null;
};

/** Receita por categoria, sem centro de custo (pedido de 20/10, item 1.5). */
export type ReceitaCategoriaMes = {
  categoria_id: string | null;
  competencia: string;
  orcado_centavos: string;
  realizado_centavos: string | null;
  comprometido_centavos: string | null;
};

export type RespostaOrcadoRealizado = {
  exercicio: number;
  natureza: string;
  realizado_disponivel: boolean;
  linhas: OrcadoRealizadoLinha[];
  /** Só na despesa. */
  conciliacao?: ConciliacaoCoordenada[];
  /** Só na receita. */
  receita_cc_parcial?: boolean;
  por_categoria?: ReceitaCategoriaMes[];
  categorias?: { categoria_id: string; codigo: string | null; nome: string }[];
};

export const buscarOrcadoRealizado = (exercicio: number, natureza: "despesa" | "receita") =>
  api.get<RespostaOrcadoRealizado>(
    `/api/orcado-realizado?exercicio=${exercicio}&natureza=${natureza}`,
  );

/** `centroCustoId` nulo: a categoria em todos os CCs que a pessoa vê. */
export const buscarLancamentosDaCoordenada = (
  centroCustoId: string | null,
  categoriaId: string | null,
  competencia: string,
) => {
  const p = new URLSearchParams({
    centro_custo_id: centroCustoId ?? "",
    categoria_id: categoriaId ?? "",
    competencia,
  });
  return api.get<{ lancamentos: Lancamento[] }>(`/api/orcado-realizado/lancamentos?${p}`);
};

/** O realizado de uma coordenada por fornecedor e mês (077). `restrito`: a
 *  categoria de pessoal para quem não tem acesso total — uma linha, sem nome. */
export type RealizadoFornecedor = {
  fornecedor_id: string | null;
  fornecedor_nome: string | null;
  restrito: boolean;
  competencia: string;
  realizado_centavos: string;
  comprometido_centavos: string;
};

/** O orçado por pessoa ou fornecedor (080), das subcategorias ligadas. */
export type OrcadoFornecedor = {
  fornecedor_id: string | null;
  fornecedor_nome: string | null;
  competencia: string;
  orcado_centavos: string;
};

export const buscarRealizadoPorFornecedor = (exercicio: number, centroCustoId: string, categoriaId: string | null) => {
  const p = new URLSearchParams({
    exercicio: String(exercicio),
    centro_custo_id: centroCustoId,
    categoria_id: categoriaId ?? "",
  });
  return api.get<{ exercicio: number; linhas: RealizadoFornecedor[]; orcado: OrcadoFornecedor[] }>(`/api/orcado-realizado/fornecedores?${p}`);
};

export type Solicitacao = {
  id: string;
  tipo: "aumento" | "remanejamento";
  modo: "linhas" | "meses" | "ano" | null;
  estado: "aberta" | "aprovada" | "rejeitada";
  criado_em: string;
  atualizado_em: string;
  motivo: string | null;
  valor_atual_centavos: string | null;
  valor_solicitado_centavos: string;
  valor_aprovado_centavos: string | null;
  linha_id: string | null;
  competencia: string;
  centro_custo_id: string;
  centro_custo_codigo: string;
  centro_custo_nome: string;
  categoria_id: string;
  categoria_codigo: string | null;
  categoria_nome: string;
  solicitante_nome: string | null;
  /** Só no remanejamento: para onde o valor vai. */
  destino: {
    centro_custo_codigo: string;
    centro_custo_nome: string;
    categoria_nome: string;
    competencias: string[];
  } | null;
  /** Só no remanejamento: de quais meses o valor sai. */
  competencias_origem?: string[] | null;
  parecer: string | null;
  decidido_por_nome: string | null;
  decidido_em: string | null;
};

export const buscarSolicitacoes = () => api.get<{ solicitacoes: Solicitacao[] }>("/api/solicitacoes");

export const decidirSolicitacao = (
  id: string,
  corpo: { decisao: "aprovar" | "recusar"; parecer: string; valor_aprovado_centavos?: string | null },
) => api.post<{ aviso: string | null }>(`/api/solicitacoes/${id}/decidir`, corpo);

export type LadoRemanejamento = { centro_custo_id: string; categoria_id: string; competencias: string[] };

export const solicitarRemanejamento = (corpo: {
  modo: "linhas" | "meses" | "ano";
  origem: LadoRemanejamento;
  destino: LadoRemanejamento;
  valor_centavos: string;
  motivo: string;
}) => api.post<{ solicitacao_id: string; aviso: string }>("/api/remanejamentos", corpo);

export type Contadores = { solicitacoes: number; pendencias: number };
export const buscarContadores = () => api.get<Contadores>("/api/contadores");

export type Aviso = {
  /** Chave estável do aviso — é o que se marca como lido. */
  chave: string;
  /** "ferramenta": pendências automáticas de Ferramentas (070). */
  tipo: "solicitacao" | "pendencia" | "ferramenta";
  titulo: string;
  texto: string;
  criado_em: string;
  lido: boolean;
  /** Solicitação a abrir ao clicar; nulo para pendência. */
  solicitacao_id: string | null;
};
export const buscarAvisos = () => api.get<{ avisos: Aviso[] }>("/api/avisos");
export const marcarAvisosLidos = (chaves: string[] | null) =>
  api.post<{ marcados: number }>("/api/avisos/lidos", { chaves });

// ─── comissão de professores (api/src/rotas/comissao.ts) ─────────────────────
// Dinheiro como string de centavos, como no resto. Percentual em pontos-base
// (2000 = 20%), inteiro.

export type CanalComissao = "pix" | "cartao" | "boleto" | "tmb";
/** adiantamento: a regra do sistema antigo, a vigente. recebido_no_mes: só o que caiu no mês. */
export type RegraComissao = "adiantamento" | "recebido_no_mes";

export type ProdutoComissao = {
  id: string;
  nome: string;
  parceiro: string | null;
  percentual_pb: number;
  guru_produtos: string[];
  grupo_tmb: string | null;
  ativo: boolean;
  regra: RegraComissao;
  ultimo_mes_fechado?: string | null;
};

export type ItemComissao = {
  canal: CanalComissao;
  referencia: string;
  data: string;
  valor_centavos: string;
  /** Ainda não caiu: está no total porque o adiantamento o inclui. */
  situacao?: "a_receber";
  /** ajuste: o que mudou no fechamento anterior depois de pago. */
  tipo?: "periodo" | "ajuste";
};
type Janela = { de: string; ate: string };

export type ApuracaoComissao =
  | {
      fechada: false;
      produto: ProdutoComissao;
      mes: string;
      regra: RegraComissao;
      /** O último mês fechado, que esta prévia acerta. */
      anterior: { mes: string; fechada_em: string; observacao: string | null } | null;
      avisos: string[];
      linhagem: { guru: string | null; pagarme: string | null; tmb: string };
      apuracao: {
        janelas: { pix: Janela; tmb: Janela; recebiveis: Janela };
        percentual_pb: number;
        /** No adiantamento, `a_receber_centavos` está DENTRO de `valor_centavos`; na outra regra, fora. */
        canais: Record<CanalComissao, { quantidade: number; valor_centavos: string; a_receber_centavos: string }>;
        total_centavos: string;
        comissao_centavos: string;
        itens: ItemComissao[];
      };
    }
  | {
      fechada: true;
      produto: ProdutoComissao;
      mes: string;
      apuracao: {
        id: string;
        janelas: { pix?: Janela; tmb?: Janela; recebiveis?: Janela };
        percentual_pb: number;
        pix_qtd: number; pix_centavos: string;
        cartao_qtd: number; cartao_centavos: string;
        boleto_qtd: number; boleto_centavos: string;
        tmb_qtd: number; tmb_centavos: string;
        regra: RegraComissao;
        ajuste_centavos: string;
        motivo_ajuste: string | null;
        observacao: string | null;
        total_centavos: string;
        comissao_centavos: string;
        fechada_em: string;
        fechada_por_nome: string | null;
        itens: ItemComissao[];
      };
    };

export type ApuracaoNoHistorico = {
  id: string;
  mes: string;
  regra: RegraComissao;
  total_centavos: string;
  ajuste_centavos: string;
  comissao_centavos: string;
  percentual_pb: number;
  observacao: string | null;
  fechada_em: string;
  fechada_por_nome: string | null;
  reaberta_em: string | null;
  motivo_reabertura: string | null;
};

export const buscarProdutosComissao = () => api.get<{ produtos: ProdutoComissao[] }>("/api/comissao/produtos");
export const buscarApuracaoComissao = (produto: string, mes: string) =>
  api.get<ApuracaoComissao>(`/api/comissao/apuracao?produto=${encodeURIComponent(produto)}&mes=${mes}`);
export const buscarHistoricoComissao = (produto: string) =>
  api.get<{ apuracoes: ApuracaoNoHistorico[] }>(`/api/comissao/apuracoes?produto=${encodeURIComponent(produto)}`);
export const buscarProdutosGuru = (termo: string) =>
  api.get<{ sugestao: string; produtos: { id: string; nome: string; sugerido: boolean }[] }>(
    `/api/comissao/guru/produtos?termo=${encodeURIComponent(termo)}`);
export const buscarGruposTmb = () => api.get<{ grupos: string[] }>("/api/comissao/tmb/grupos");

// ─── Ferramentas e assinaturas (migrations 065 e 066) ────────────────────────

export type Ferramenta = {
  id: string;
  nome: string;
  tipo: string | null;
  status: "ativa" | "verificar" | "nova" | "cancelada";
  moeda: "BRL" | "USD";
  /** Os valores da planilha, cada um na sua coluna, como informados. */
  valor_mensal_centavos: string | null;
  valor_trimestral_centavos: string | null;
  valor_semestral_centavos: string | null;
  valor_anual_centavos: string | null;
  valor_total_ano_centavos: string | null;
  valor_usd_centavos: string | null;
  /** Texto, como na planilha ("Mensal", "Anual parcelado 12x"). */
  periodicidade: string | null;
  tipo_cobranca: string | null;
  forma_pagamento: "cartao" | "boleto" | "pix" | "transferencia" | "outro" | null;
  cartao_final: string | null;
  /** Texto, como na planilha ("5", "Variado", "20/01/2027"). */
  dia_vencimento: string | null;
  dia_cobranca: number | null;
  categoria_id: string | null;
  centro_custo_id: string | null;
  nome_extrato: string | null;
  termos_busca: string[];
  responsavel: string | null;
  usuarios_ativos: string | null;
  qtd_usuarios: number | null;
  uso: string | null;
  url_acesso: string | null;
  renovacao: "automatica" | "manual" | null;
  proxima_renovacao: string | null;
  teste: boolean;
  teste_ate: string | null;
  cancelada_em: string | null;
  observacao: string | null;
  tendencia: string | null;
  /** Texto de célula que não cabe na coluna tipada, por coluna. */
  texto_planilha: Record<string, string>;
  avisos_importacao: string[];
  origem: "manual" | "planilha";
  origem_ref: string | null;
  linha_planilha: number | null;
  centro_custo_codigo: string | null;
  centro_custo_nome: string | null;
  categoria_codigo: string | null;
  categoria_nome: string | null;
  /** O ciclo de cobrança (067). Sem ele, valem os valores da planilha, e a ferramenta fica para conferir. */
  ciclo: Ciclo | null;
  /** O valor de UM ciclo, na moeda da ferramenta: centavos de real ou de dólar. */
  valor_ciclo_centavos: string | null;
  /** "planilha": tirado da planilha, recalculado a cada gravação. "manual": escolhido no cadastro. */
  ciclo_fonte: "planilha" | "manual" | null;
  /** O valor do ciclo em reais: em dólar, × PTAX × (1 + acréscimo). */
  valor_ciclo_reais_centavos: string | null;
  meses_ciclo: number | null;
  /** A cotação usada, só nas ferramentas em dólar. */
  cotacao_data: string | null;
  cotacao_venda: string | null;
  cotacao_acrescimo: string | null;
  /** O "Valor Mensal Estimado": com ciclo, o do ciclo ÷ meses; sem ciclo, o da planilha. Pontual não tem. */
  custo_mensal_centavos: string | null;
  /** O custo no ano: o do ciclo × ciclos no ano; pontual, a compra. Só com ciclo. */
  custo_anual_centavos: string | null;
  /** A função, numa lista fechada (069). Pré-classificada; o Financeiro corrige no cadastro. */
  familia: Familia | null;
  aba: "controle_geral" | "cancelados";
  /** Só verdadeiro para quem vê o cofre. */
  tem_acesso: boolean;
};

export type PendenciaFerramenta = {
  id: string;
  secao: "pendencias" | "atencoes_criticas";
  ferramenta_id: string | null;
  ferramenta_texto: string;
  urgencia: "critico" | "urgente" | "medio" | "baixo" | null;
  motivo: string | null;
  acao_sugerida: string | null;
  prazo: string | null;
  responsavel: string | null;
  resolvido: boolean;
  resolvido_em: string | null;
  origem: "manual" | "planilha";
  linha_planilha: number | null;
  /** "cobranca_sem_ferramenta": a cobrança que não casa com ferramenta do cadastro (068). */
  tipo: "geral" | "cobranca_sem_ferramenta" | "valor_mudou" | "cobranca_cancelada" | "cobranca_ausente" | "renovacao" | "termino";
  /** O fornecedor da cobrança sem ferramenta, ou do grupo do alerta (070). */
  chave_cobranca: string | null;
  /** A identidade do alerta automático (070); nula na pendência escrita por pessoa. */
  chave_alerta: string | null;
  /** A Nova dos CSVs de onde saiu; cadastrá-la fecha a pendência. */
  candidata_id: string | null;
};

export type CandidataFerramenta = {
  id: string;
  ferramenta_texto: string;
  descritivo_extrato: string | null;
  faturas: string | null;
  valores_observados: string | null;
  dia_cartao: string | null;
  classificacao: string | null;
  status_texto: string | null;
  acao: string | null;
  ferramenta_id: string | null;
  linha_planilha: number | null;
};

export type DadosFerramentas = {
  ferramentas: Ferramenta[];
  pendencias: PendenciaFerramenta[];
  candidatas: CandidataFerramenta[];
  /** `permitido`: a pessoa vê o cofre (admin). `configurado`: o ambiente tem a COFRE_CHAVE. */
  cofre: { permitido: boolean; configurado: boolean };
  /** A PTAX mais recente e o acréscimo sobre ela, em fração (067). */
  cotacao: Cotacao;
  /** As cobranças do Conta Azul reconhecidas, dos últimos 13 meses (070). Vazias para quem não tem acesso total. */
  cobrancas: Cobrancas;
  /** As excluídas (078), para restaurar. Vazia para quem não tem acesso total. */
  excluidas: FerramentaExcluida[];
};

export type FerramentaExcluida = {
  id: string;
  ferramenta_id: string;
  nome: string;
  origem: "planilha" | "manual";
  motivo: string;
  excluida_em: string;
  excluida_por_nome: string | null;
};

/** Exclui a linha que não deveria existir (078): sai de todas as telas e contas,
 *  e a planilha não a traz de volta. Cancelar é outra coisa. */
export const excluirFerramenta = (id: string, motivo: string) =>
  api.post<{ resultado: unknown }>(`/api/ferramentas/${id}/excluir`, { motivo });
export const restaurarFerramenta = (excluidaId: string) =>
  api.post<{ ferramenta_id: string }>(`/api/ferramentas/excluidas/${excluidaId}/restaurar`, {});

export type GrupoCobranca = {
  /** O fornecedor: a primeira palavra útil da descrição da cobrança. */
  chave: string;
  ferramentas: string[];
  ativas: number;
  canceladas: number;
  esperado_mensal_centavos: string;
  todas_mensais: boolean;
};
export type CobrancaMes = {
  chave: string;
  /** "AAAA-MM". */
  mes: string;
  cobrancas: number;
  real_centavos: string;
  iof_centavos: string;
  sem_ferramenta: boolean;
};
export type CobrancaFerramentaMes = {
  ferramenta_id: string;
  mes: string;
  cobrancas: number;
  real_centavos: string;
  iof_centavos: string;
};
export type Cobrancas = { grupos: GrupoCobranca[]; meses: CobrancaMes[]; porFerramenta: CobrancaFerramentaMes[] };

/** O mesmo que o Cron Job faz toda noite: reconhece as cobranças e abre as pendências (070). */
export const reconhecerCobrancas = () => api.post<unknown>("/api/ferramentas/reconhecer", {});
/** O Financeiro ensina: o fornecedor é da ferramenta X, ou não é ferramenta (070). */
export const ensinarCobranca = (dados: { chave: string; ferramenta_id?: string | null; nao_e_ferramenta?: boolean }) =>
  api.post<unknown>("/api/ferramentas/ensinar", dados);

export const buscarFerramentas = () => api.get<DadosFerramentas>("/api/ferramentas");

export type FerramentaEdicao = Omit<Ferramenta,
  "id" | "origem" | "origem_ref" | "linha_planilha" | "centro_custo_codigo" | "centro_custo_nome" | "categoria_codigo"
  | "categoria_nome" | "custo_mensal_centavos" | "aba" | "tem_acesso" | "ciclo_fonte" | "valor_ciclo_reais_centavos"
  | "meses_ciclo" | "cotacao_data" | "cotacao_venda" | "cotacao_acrescimo" | "custo_anual_centavos"
> & { id?: string; candidata_id?: string };

/** Ferramentas × orçamento: o orçado e o realizado do exercício nas coordenadas com ferramenta. */
export const buscarOrcamentoFerramentas = (exercicio: number) =>
  api.get<{ exercicio: number; linhas: LinhaOrcamento[] }>(`/api/ferramentas/orcamento?exercicio=${exercicio}`);

/** O acréscimo sobre a PTAX, em fração: 0.035 é 3,5%. Só financeiro e admin. */
export const definirAcrescimoCotacao = (valor: number) => api.post<{ ok: true }>("/api/ferramentas/acrescimo-cotacao", { valor });

export const salvarFerramenta = (f: FerramentaEdicao) => api.post<{ id: string }>("/api/ferramentas", f);

export type ResultadoImportacaoFerramentas = {
  informadas: number; importadas: number; atualizadas: number; mantidas: number; iguais: number;
  /** Linhas da planilha que o Financeiro tinha excluído, e que a importação pulou (078). */
  excluidas?: number;
  pendencias: number; pendencias_ja_existiam: number; candidatas: number; candidatas_ja_existiam: number;
};

export const importarFerramentas = (dados: {
  arquivo: string;
  itens: (Partial<FerramentaEdicao> & { origem_ref: string })[];
  pendencias: unknown[];
  candidatas: unknown[];
}) => api.post<ResultadoImportacaoFerramentas>("/api/ferramentas/importar", dados);

export type PendenciaEdicao = Omit<PendenciaFerramenta,
  "id" | "resolvido_em" | "origem" | "linha_planilha" | "tipo" | "candidata_id" | "chave_cobranca" | "chave_alerta"> & { id?: string };
export const salvarPendenciaFerramenta = (p: PendenciaEdicao) => api.post<{ id: string }>("/api/ferramentas/pendencias", p);

// O cofre: só para quem `cofre.permitido`. Cada revelação vai para o Histórico.
export const revelarAcessoFerramenta = (id: string) =>
  api.post<{ login: string | null; senha: string | null }>(`/api/ferramentas/${id}/acesso/revelar`, {});
/** Campo ausente: fica como está. `null`: apaga. */
export const salvarAcessoFerramenta = (id: string, acesso: { login?: string | null; senha?: string | null }) =>
  api.post<{ ok: true }>(`/api/ferramentas/${id}/acesso`, acesso);
export const importarAcessosFerramentas = (itens: { origem_ref: string; login: string | null; senha: string | null }[]) =>
  api.post<{ gravados: number; ja_tinham: number; sem_ferramenta: number }>("/api/ferramentas/acessos/importar", { itens });

// ── Ferramentas da área e o pedido de ferramenta (072) ─────────────────────

export type TipoPedidoFerramenta = "nova" | "cancelar" | "alterar";
export type EstadoPedidoFerramenta = "aberto" | "encaminhado" | "aprovado" | "recusado";

export type PedidoFerramenta = {
  id: string;
  tipo: TipoPedidoFerramenta;
  centro_custo_id: string;
  centro_custo_codigo: string;
  centro_custo_nome: string;
  /** Cancelar e alterar: a ferramenta da área. Nova: a criada na aprovação. */
  ferramenta_id: string | null;
  ferramenta_nome: string;
  ferramenta_status: Ferramenta["status"] | null;
  nome: string | null;
  motivo: string;
  responsavel: string | null;
  moeda: "BRL" | "USD" | null;
  ciclo: Ciclo | null;
  /** O valor de um ciclo, na moeda do pedido. */
  valor_ciclo_centavos: string | null;
  a_partir_de: string | null;
  teste: boolean;
  url: string | null;
  estado: EstadoPedidoFerramenta;
  encaminhado_a: string | null;
  encaminhamento: string | null;
  encaminhado_em: string | null;
  aprovado_por: string | null;
  parecer: string | null;
  decidido_em: string | null;
  decidido_por_nome: string | null;
  solicitante_nome: string | null;
  criado_em: string;
  /** O cadastro de hoje, para o "alterar" mostrar o antes. */
  atual_moeda: "BRL" | "USD" | null;
  atual_ciclo: Ciclo | null;
  atual_valor_ciclo_centavos: string | null;
  atual_custo_mensal_centavos: string | null;
};

/**
 * O cobrado numa área e num mês, só a parte lançada no centro de custo da área
 * (072, 073): de uma ferramenta (`ferramenta_id`), ou do grupo do fornecedor
 * que várias dividem (`ferramenta_id` nulo, com as ferramentas do grupo ali).
 */
export type CobradoArea = {
  centro_custo_id: string;
  ferramenta_id: string | null;
  chave: string;
  ferramentas: string[];
  mes: string;
  cobrancas: number;
  cobrado_centavos: string;
  iof_centavos: string;
};

export type DadosFerramentasArea = {
  areas: { id: string; codigo: string; nome: string }[];
  /** Sem o que é só do Financeiro: final do cartão, nome no extrato, observação. */
  ferramentas: Ferramenta[];
  cobrado: CobradoArea[];
  pedidos: PedidoFerramenta[];
};

export const buscarFerramentasArea = () => api.get<DadosFerramentasArea>("/api/ferramentas/area");
export const buscarPedidosFerramenta = () => api.get<{ pedidos: PedidoFerramenta[] }>("/api/ferramentas/pedidos");

export type PedidoFerramentaEdicao = {
  tipo: TipoPedidoFerramenta;
  centro_custo_id?: string;
  ferramenta_id?: string;
  nome?: string;
  motivo: string;
  responsavel?: string;
  moeda?: "BRL" | "USD";
  ciclo?: Ciclo;
  valor_ciclo_centavos?: string;
  a_partir_de?: string;
  teste?: boolean;
  url?: string;
};
export const pedirFerramenta = (p: PedidoFerramentaEdicao) => api.post<{ id: string }>("/api/ferramentas/pedidos", p);
export const encaminharPedidoFerramenta = (id: string, para: string, comentario: string) =>
  api.post<{ ok: true }>(`/api/ferramentas/pedidos/${id}/encaminhar`, { para, comentario });
export const decidirPedidoFerramenta = (id: string, d: { decisao: "aprovar" | "recusar"; parecer?: string; aprovado_por?: string }) =>
  api.post<{ estado: EstadoPedidoFerramenta; ferramenta_id: string | null }>(`/api/ferramentas/pedidos/${id}/decidir`, d);

// ── O orçamento do ano seguinte montado pelas áreas (071) ──────────────────

export type AreaProposta = {
  exercicio_ano: number;
  centro_custo_id: string;
  centro_custo_codigo: string;
  centro_custo_nome: string;
  proposta_id: string | null;
  status: StatusArea;
  linhas: number;
  enviadas: number;
  devolvidas: number;
  aprovadas: number;
  rascunho: number;
  base_centavos: string;
  pedido_centavos: string;
  aprovado_centavos: string;
  enviada_em: string | null;
  decidida_em: string | null;
};

export type LinhaProposta = {
  id: string;
  proposta_id: string;
  categoria_id: string;
  /** Nula na linha da categoria; o texto no item novo. */
  descricao: string | null;
  /** Os 12 meses, em centavos. */
  base_centavos: string[];
  pedido_centavos: string[];
  aprovado_centavos: string[] | null;
  observacao: string | null;
  situacao: SituacaoLinha;
  justificativa: string | null;
  enviada_em: string | null;
  decidida_em: string | null;
  exercicio_ano: number;
  centro_custo_id: string;
  ano_base: number;
  categoria_codigo: string | null;
  categoria_nome: string;
  base_total_centavos: string;
  pedido_total_centavos: string;
  aprovado_total_centavos: string | null;
  meses_acima: number[];
  acima_no_ano: boolean;
  /** O detalhe da linha (082). Opcionais: a API antiga não manda. */
  fornecedor_id?: string | null;
  fornecedor_nome?: string | null;
  subcategoria?: string | null;
  tipo?: "categoria" | "pessoa" | "subcategoria" | "item";
};

/** O nome da pessoa ou da subcategoria da linha; nulo na categoria e no item novo. */
export const detalheDaLinha = (l: Pick<LinhaProposta, "fornecedor_id" | "fornecedor_nome" | "subcategoria">): string | null =>
  l.subcategoria ?? (l.fornecedor_id ? l.fornecedor_nome ?? "Pessoa sem nome" : null);

export type LinhaPropostaEdicao = {
  id?: string;
  categoria_id?: string;
  descricao?: string | null;
  pedido_centavos: string[];
  observacao?: string | null;
  remover?: boolean;
};
export type DecisaoProposta = {
  linha_id: string;
  decisao: "aprovar" | "ajustar" | "devolver";
  aprovado_centavos?: string[];
  justificativa?: string;
};

export const buscarAreasProposta = () => api.get<{ areas: AreaProposta[] }>("/api/propostas/areas");
/** `categorias_da_area`: as categorias que a área pode acrescentar (081). Opcional: a API antiga não manda. */
export const buscarProposta = (id: string) =>
  api.get<{ area: AreaProposta; linhas: LinhaProposta[]; categorias_da_area?: string[] }>(`/api/propostas/${id}`);

/** Quais categorias cada área orça (081): a aba do Financeiro. */
export type CategoriaArea = { id: string; codigo: string | null; nome: string; so_financeiro: boolean; centros: string[] };
export const buscarCategoriasArea = () => api.get<{
  ano_base: number;
  categorias: CategoriaArea[];
  centros: { id: string; codigo: string; nome: string }[];
  gasto: { categoria_id: string; centros: string[] }[];
}>("/api/categorias-area");
export const configurarCategoriaArea = (categoria_id: string, so_financeiro: boolean, centro_custo_ids: string[]) =>
  api.post<{ mudou: boolean; linhas_tiradas: number }>("/api/categorias-area", { categoria_id, so_financeiro, centro_custo_ids });
export const buscarLinhasPropostas = (ano: number) => api.get<{ linhas: LinhaProposta[] }>(`/api/propostas/linhas?ano=${ano}`);
export const abrirProposta = (centro_custo_id: string, ano: number) =>
  api.post<{ id: string }>("/api/propostas/abrir", { centro_custo_id, ano });
export const salvarProposta = (id: string, linhas: LinhaPropostaEdicao[]) =>
  api.post<unknown>(`/api/propostas/${id}/salvar`, { linhas });
export const enviarProposta = (id: string) => api.post<{ enviadas: number }>(`/api/propostas/${id}/enviar`, {});
export const decidirProposta = (id: string, decisoes: DecisaoProposta[]) =>
  api.post<{ aprovadas: number; ajustadas: number; devolvidas: number }>(`/api/propostas/${id}/decidir`, { decisoes });
